import { CanActivateChildFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Guards every feature route: allows navigation when logged in, otherwise
 * redirects to the login screen.
 */
export const authGuard: CanActivateChildFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.isLoggedIn() ? true : router.createUrlTree(['/login']);
};

/**
 * User Setup (2026-08-07): while a forced password change is pending (first login, or
 * after an admin reset), the user is locked to the change-password screen until done.
 */
export const mustChangePasswordGuard: CanActivateChildFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (!auth.isLoggedIn() || !auth.mustChangePassword()) return true;
  return state.url.startsWith('/account/change-password') ? true : router.createUrlTree(['/account/change-password']);
};

// User Setup (2026-08-07): map the first URL segment to the top-level nav section key
// it belongs to, so the route guard can enforce the same department/role gating as the
// sidebar (typing a URL directly must not bypass the hidden menus).
const SEGMENT_SECTION: Record<string, string> = {
  'pss': 'transaction',
  'reports': 'reports',
  'finance': 'finance',
  'finance-reports': 'finance-reports',
  'production': 'production',
  'quality-control': 'quality-control',
  'asset-management': 'asset-management',
  'hrm': 'hrm',
  'hrm-reports': 'hrm-reports',
  'crm': 'crm',
  'sales-performance': 'sales-performance',
  'csc': 'csc',
  'pos': 'pos',
  'online-store': 'online-store',
  'configuration': 'configuration',
  'administration': 'administration',
  'approvals': 'approvals',
  'self-service': 'self-service'
};

/**
 * Enforces department / role-level section access on direct navigation. If the target
 * route belongs to a section the current login may not see, it is bounced to the
 * dashboard. Personal/utility routes (dashboard, account, unmapped) are always allowed.
 */
export const permissionGuard: CanActivateChildFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (!auth.isLoggedIn()) return true; // authGuard handles the redirect to /login

  const first = state.url.replace(/^\/+/, '').split(/[/?#]/)[0];
  if (!first) return true;                      // dashboard (landing)
  const section = SEGMENT_SECTION[first];
  if (!section) return true;                     // unmapped / personal routes (e.g. /account)
  return auth.canSeeSection(section) ? true : router.createUrlTree(['/']);
};
